import { useState, useEffect, useCallback, useRef } from 'react';
import { RestOutlined, FireOutlined, CheckOutlined, DownOutlined } from '@ant-design/icons';
import * as api from '../../services/api';
import './Today.css';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function getTodayIndex() {
  return new Date().getDay();
}

function getTodayDate() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function Today() {
  const [restDays, setRestDays] = useState<Record<number, boolean>>({});
  const [workouts, setWorkouts] = useState<api.TodayWorkout[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  // Local input state to prevent re-renders while typing
  const [localInputs, setLocalInputs] = useState<Record<string, string>>({});
  const debounceTimers = useRef<Record<string, NodeJS.Timeout>>({});

  const todayIndex = getTodayIndex();
  const todayDate = getTodayDate();
  const isRestDay = restDays[todayIndex] || false;

  const fetchWorkouts = useCallback(async () => {
    try {
      const data = await api.getTodayWorkouts(todayDate);
      setWorkouts(data);
    } catch (err) {
      console.error(err);
    }
  }, [todayDate]);

  // Fetch rest days on mount
  useEffect(() => {
    api.getRestDays().then(days => {
      const restDaysMap: Record<number, boolean> = {};
      days.forEach(d => restDaysMap[d] = true);
      setRestDays(restDaysMap);
    }).catch(console.error);
  }, []);

  // Fetch today's workouts on mount
  useEffect(() => {
    setLoading(true);
    fetchWorkouts().finally(() => setLoading(false));
  }, [fetchWorkouts]);

  const toggleRestDay = async () => {
    const newIsRest = !isRestDay;
    setRestDays(prev => ({ ...prev, [todayIndex]: newIsRest }));
    try {
      await api.setRestDay(todayIndex, newIsRest);
    } catch (err) {
      // Revert on error
      setRestDays(prev => ({ ...prev, [todayIndex]: !newIsRest }));
      console.error(err);
    }
  };

  const toggleExpanded = (sessionId: number) => {
    setExpandedId(prev => prev === sessionId ? null : sessionId);
  };

  // Handle local input change (immediate, no API call)
  const handleLocalInputChange = (
    sessionId: number,
    setNumber: number,
    field: 'weight' | 'reps',
    value: string
  ) => {
    const inputKey = `${sessionId}-${setNumber}-${field}`;
    setLocalInputs(prev => ({ ...prev, [inputKey]: value }));

    // Clear existing debounce timer
    if (debounceTimers.current[inputKey]) {
      clearTimeout(debounceTimers.current[inputKey]);
    }

    // Debounce the API call
    debounceTimers.current[inputKey] = setTimeout(() => {
      const numValue = field === 'weight' ? parseFloat(value) || 0 : parseInt(value) || 0;
      handleSetUpdate(sessionId, setNumber, field, numValue);
    }, 800);
  };

  const handleSetUpdate = async (
    sessionId: number,
    setNumber: number,
    field: 'weight' | 'weightUnit' | 'reps',
    value: number | string
  ) => {
    // Find current set to check if we should auto-complete
    const currentWorkout = workouts.find(w => w.sessionId === sessionId);
    const currentSet = currentWorkout?.sets.find(s => s.setNumber === setNumber);

    // Determine if set should be auto-completed (has reps entered)
    let shouldComplete = currentSet?.completed || false;
    if (field === 'reps' && typeof value === 'number' && value > 0) {
      shouldComplete = true;
    } else if (field !== 'reps' && currentSet && currentSet.reps > 0) {
      shouldComplete = true;
    }

    // Optimistic update
    setWorkouts(prev => prev.map(workout => {
      if (workout.sessionId !== sessionId) return workout;
      const newSets = workout.sets.map(set => {
        if (set.setNumber !== setNumber) return set;
        return { ...set, [field]: value, completed: shouldComplete };
      });
      const allCompleted = newSets.every(s => s.completed);
      const totalPoints = newSets.reduce((sum, s) => {
        // Bodyweight + kg conversion
        if (s.weight === 0) return sum + s.reps;
        const weightInLbs = s.weightUnit === 'kg' ? s.weight * 2.205 : s.weight;
        return sum + Math.round(weightInLbs * s.reps);
      }, 0);
      return { ...workout, sets: newSets, completed: allCompleted, totalPoints };
    }));

    // Clear local input after updating workouts state
    const inputKey = `${sessionId}-${setNumber}-${field}`;
    setLocalInputs(prev => {
      const newInputs = { ...prev };
      delete newInputs[inputKey];
      return newInputs;
    });

    try {
      const payload: Record<string, number | string | boolean> = { [field]: value };
      if (shouldComplete && !currentSet?.completed) {
        payload.completed = true;
      }
      // Fire and forget - don't update state from server to avoid re-render
      await api.updateTodaySet(sessionId, setNumber, payload);
    } catch (err) {
      console.error(err);
      // Refresh to get correct state on error
      fetchWorkouts();
    }
  };

  const handleSetComplete = async (sessionId: number, setNumber: number, completed: boolean) => {
    // Optimistic update
    setWorkouts(prev => prev.map(workout => {
      if (workout.sessionId !== sessionId) return workout;
      const newSets = workout.sets.map(set =>
        set.setNumber === setNumber ? { ...set, completed } : set
      );
      const allCompleted = newSets.every(s => s.completed);
      const totalPoints = newSets.reduce((sum, s) => {
        // Bodyweight + kg conversion
        if (s.weight === 0) return sum + s.reps;
        const weightInLbs = s.weightUnit === 'kg' ? s.weight * 2.205 : s.weight;
        return sum + Math.round(weightInLbs * s.reps);
      }, 0);
      return { ...workout, sets: newSets, completed: allCompleted, totalPoints };
    }));

    try {
      // Fire and forget - don't update state from server to avoid re-render
      await api.updateTodaySet(sessionId, setNumber, { completed });
    } catch (err) {
      console.error(err);
      fetchWorkouts();
    }
  };

  const calculateSetPoints = (weight: number, reps: number, weightUnit: 'lbs' | 'kg' = 'lbs') => {
    // Bodyweight: if weight=0, count just reps
    if (weight === 0) return reps;
    // Convert kg to lbs for points (1 kg = 2.205 lbs)
    const weightInLbs = weightUnit === 'kg' ? weight * 2.205 : weight;
    return Math.round(weightInLbs * reps);
  };

  const totalSets = workouts.reduce((sum, w) => sum + w.setsTarget, 0);
  const completedSets = workouts.reduce((sum, w) => sum + w.sets.filter(s => s.completed).length, 0);
  const totalPoints = workouts.reduce((sum, w) => {
    // Calculate from sets with kg conversion
    return sum + w.sets.reduce((setSum, s) => {
      return setSum + calculateSetPoints(s.weight, s.reps, s.weightUnit);
    }, 0);
  }, 0);

  return (
    <div className="today-page">
      <div className="today-header">
        <div className="today-title">
          <h1>{DAYS[todayIndex]}</h1>
          <span className="today-badge">Today</span>
        </div>
        <div className="rest-toggle">
          <span className="rest-label">Rest Day</span>
          <button
            className={`toggle-switch ${isRestDay ? 'on' : ''}`}
            onClick={toggleRestDay}
          >
            <span className="toggle-knob" />
          </button>
        </div>
      </div>

      {!isRestDay && (
        <div className="today-content">
          {loading ? (
            <div className="empty-state">
              <p>Loading...</p>
            </div>
          ) : workouts.length === 0 ? (
            <div className="empty-state">
              <FireOutlined className="empty-icon" />
              <p>No workouts scheduled for today</p>
              <span className="empty-hint">Add workouts in the Workouts tab</span>
            </div>
          ) : (
            <>
              <div className="workout-summary">
                <div className="summary-left">
                  <span className="workout-count">{workouts.length} workout{workouts.length !== 1 ? 's' : ''}</span>
                  <span className="total-sets">{completedSets}/{totalSets} sets</span>
                </div>
                <div className="summary-right">
                  <span className="total-points">{totalPoints.toLocaleString()} pts</span>
                </div>
              </div>

              <div className="workout-list">
                {workouts.map(workout => {
                  const isExpanded = expandedId === workout.sessionId;
                  const completedCount = workout.sets.filter(s => s.completed).length;

                  return (
                    <div key={workout.sessionId} className={`today-workout-card ${workout.completed ? 'completed' : ''}`}>
                      <div
                        className="workout-header-row"
                        onClick={() => toggleExpanded(workout.sessionId)}
                      >
                        <div className="completion-indicator">
                          {workout.completed ? (
                            <div className="circle-check">
                              <CheckOutlined />
                            </div>
                          ) : (
                            <div className="circle-empty">
                              <span className="progress-text">{completedCount}/{workout.setsTarget}</span>
                            </div>
                          )}
                        </div>
                        <div className="workout-info">
                          <h4>{workout.name}</h4>
                          <div className="workout-meta">
                            <span>{workout.setsTarget} sets</span>
                            {workout.bodyParts && workout.bodyParts.length > 0 && (
                              <span className="workout-tags">{workout.bodyParts.join(', ')}</span>
                            )}
                            {workout.totalPoints > 0 && (
                              <span className="points-badge">{workout.totalPoints.toLocaleString()} pts</span>
                            )}
                          </div>
                        </div>
                        <div className={`expand-arrow ${isExpanded ? 'open' : ''}`}>
                          <DownOutlined />
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="sets-container">
                          {workout.sets.map(set => {
                            const setPoints = calculateSetPoints(set.weight, set.reps, set.weightUnit);

                            return (
                              <div key={set.setNumber} className={`set-row ${set.completed ? 'completed' : ''}`}>
                                <div className="set-header">
                                  <span className="set-label">Set {set.setNumber}</span>
                                  <button
                                    className={`set-complete-btn ${set.completed ? 'done' : ''}`}
                                    onClick={() => handleSetComplete(workout.sessionId, set.setNumber, !set.completed)}
                                  >
                                    {set.completed ? <CheckOutlined /> : ''}
                                  </button>
                                </div>

                                <div className="set-inputs">
                                  <div className="input-field">
                                    <label>Weight</label>
                                    <div className="weight-input-group">
                                      <input
                                        type="number"
                                        inputMode="decimal"
                                        value={localInputs[`${workout.sessionId}-${set.setNumber}-weight`] ?? (set.weight || '')}
                                        placeholder="0"
                                        onChange={(e) => handleLocalInputChange(
                                          workout.sessionId,
                                          set.setNumber,
                                          'weight',
                                          e.target.value
                                        )}
                                      />
                                      <div className="unit-toggle">
                                        <button
                                          className={`unit-btn ${set.weightUnit === 'lbs' ? 'active' : ''}`}
                                          onClick={() => handleSetUpdate(workout.sessionId, set.setNumber, 'weightUnit', 'lbs')}
                                        >
                                          lbs
                                        </button>
                                        <button
                                          className={`unit-btn ${set.weightUnit === 'kg' ? 'active' : ''}`}
                                          onClick={() => handleSetUpdate(workout.sessionId, set.setNumber, 'weightUnit', 'kg')}
                                        >
                                          kg
                                        </button>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="input-field">
                                    <label>Reps</label>
                                    <input
                                      type="number"
                                      inputMode="numeric"
                                      value={localInputs[`${workout.sessionId}-${set.setNumber}-reps`] ?? (set.reps || '')}
                                      placeholder="0"
                                      onChange={(e) => handleLocalInputChange(
                                        workout.sessionId,
                                        set.setNumber,
                                        'reps',
                                        e.target.value
                                      )}
                                    />
                                  </div>

                                  <div className="set-points">
                                    <span className="points-value">{setPoints.toLocaleString()}</span>
                                    <span className="points-label">pts</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {isRestDay && (
        <div className="rest-content">
          <RestOutlined className="rest-icon" />
          <p>Rest day - recover and recharge!</p>
        </div>
      )}
    </div>
  );
}
